import React, { useState, useEffect } from "react";
import { useI18n } from "../../i18n";
import api from "../../api";
import MethodologyDisclaimer from "../MethodologyDisclaimer";

const DAY_TYPE_OPTIONS = ["hard_training", "light_training", "match", "rest", "double_session"];

const WeeklyPlan = ({ profile, weeklyData: weeklyFromParent, onDayTypeChange }) => {
  const { t, lang } = useI18n();
  const [localData, setLocalData] = useState(null);
  const [openDay, setOpenDay] = useState(null);

  // Prefer the centrally-managed weeklyData from Dashboard (so any day-type
  // override propagates here AND to the Sumar at the same time). Fallback to
  // a local fetch so this tab still works if it is ever rendered standalone.
  useEffect(() => {
    if (weeklyFromParent || !profile) return;
    api.post("/plan/weekly", { profile }).then((r) => setLocalData(r.data));
  }, [profile, weeklyFromParent]);

  const data = weeklyFromParent || localData;
  const isLifestyle = profile?.profile_type === "lifestyle" || profile?.sport === "lifestyle";

  if (!data) return <div className="text-white/60">{t.common.loading}</div>;

  const handleChange = (dayKey, newType) => {
    if (onDayTypeChange) onDayTypeChange(dayKey, newType);
  };

  return (
    <div>
      <div className="grid md:grid-cols-7 gap-2 mb-6">
        {data.weekly_plan.days.map((d) => (
          <div
            key={d.day_key}
            className="p-4"
            data-testid={`weekly-day-${d.day_key}`}
            style={{
              background: openDay === d.day_key ? "#181111" : "#141414",
              border: `1px solid ${openDay === d.day_key ? "#FF3B30" : "#27272A"}`,
            }}
          >
            <div className="font-mono text-[10px] tracking-widest text-white/50">{t.plan.days[d.day_key]}</div>
            {isLifestyle ? (
              <div className="font-mono text-[9px] tracking-widest text-[#FF3B30] mt-1">
                {t.plan.day_types[d.day_type]}
              </div>
            ) : (
              <select
                value={d.day_type}
                onChange={(e) => handleChange(d.day_key, e.target.value)}
                onClick={(e) => e.stopPropagation()}
                data-testid={`weekly-day-type-select-${d.day_key}`}
                className="mt-1 w-full bg-transparent font-mono text-[10px] tracking-widest text-[#FF3B30] outline-none cursor-pointer"
                style={{ border: "1px solid #2A1414", padding: "2px 4px" }}
              >
                {DAY_TYPE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt} style={{ background: "#141414", color: "#fff" }}>
                    {t.plan.day_types_short?.[opt] || t.plan.day_types[opt]}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              onClick={() => setOpenDay(openDay === d.day_key ? null : d.day_key)}
              data-testid={`weekly-day-open-${d.day_key}`}
              className="text-left w-full mt-2"
              style={{ background: "transparent", cursor: "pointer" }}
            >
              <div className="stat-num text-2xl">{Math.round(d.totals.kcal)}</div>
              <div className="font-mono text-[10px] text-white/40">kcal</div>
            </button>
          </div>
        ))}
      </div>

      {openDay && (() => {
        const d = data.weekly_plan.days.find((x) => x.day_key === openDay);
        if (!d) return null;
        const cn = d.coach_note;
        const showCoach = cn?.type && cn.type !== "balanced";
        const isUp = showCoach && cn.type.includes("up");
        return (
          <div className="sne-card">
            <h3 className="font-heading text-2xl mb-4">
              {t.plan.days[d.day_key]} · {t.plan.day_types[d.day_type]}
            </h3>
            <div className="space-y-3">
              {d.meals.map((m) => (
                <div key={m.id} className="pb-3" style={{ borderBottom: "1px solid #1F1F1F" }}>
                  <div className="flex justify-between items-baseline mb-2">
                    <div className="font-heading text-lg">{m[`title_${lang}`] || m.title_en}</div>
                    <div className="font-mono text-xs text-white/60">{Math.round(m.totals.kcal)} kcal</div>
                  </div>
                  <div className="font-mono text-xs text-white/60">
                    {m.foods.map((f) => `${f[`name_${lang}`] || f.name_en} ${Math.round(f.grams)}g`).join(" · ")}
                  </div>
                </div>
              ))}
            </div>
            {showCoach && (
              <div
                data-testid={`weekly-coach-note-${d.day_key}`}
                className="mt-5 p-4"
                style={{
                  background: isUp ? "#1A1410" : "#0E1418",
                  border: `1px solid ${isUp ? "#4A3520" : "#1E3548"}`,
                  borderLeft: `3px solid ${isUp ? "#F59E0B" : "#007AFF"}`,
                  color: isUp ? "#FFE0B5" : "#D5E5FF",
                }}
              >
                <div className="font-mono text-[10px] tracking-widest mb-2"
                     style={{ color: isUp ? "#F59E0B" : "#007AFF" }}>
                  {lang === "ro" ? "NOTĂ COACH" : "COACH NOTE"} · {cn.delta_kcal > 0 ? "+" : ""}{cn.delta_kcal} kcal
                </div>
                <div className="font-heading text-lg mb-2">
                  {lang === "ro" ? cn.title_ro : cn.title_en}
                </div>
                <div className="text-sm leading-relaxed">
                  {lang === "ro" ? cn.body_ro : cn.body_en}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      <div className="mt-6 sne-card flex flex-wrap gap-6">
        <div>
          <div className="font-mono text-xs tracking-widest text-white/50">WEEK TOTALS</div>
          <div className="stat-num text-3xl" data-testid="weekly-total-kcal">{data.weekly_plan.week_totals.kcal}</div>
          <div className="font-mono text-xs text-white/40">kcal</div>
        </div>
        <div>
          <div className="font-mono text-xs tracking-widest text-white/50">{lang === "ro" ? "MEDIA / ZI" : "DAILY AVG"}</div>
          <div className="stat-num text-3xl" data-testid="weekly-avg-kcal">
            {Math.round(data.weekly_plan.week_totals.kcal / 7)}
          </div>
          <div className="font-mono text-xs text-white/40">kcal</div>
        </div>
        <div>
          <div className="font-mono text-xs tracking-widest text-white/50">PROTEIN</div>
          <div className="stat-num text-3xl" style={{ color: "#FF3B30" }}>{data.weekly_plan.week_totals.protein}g</div>
        </div>
        <div>
          <div className="font-mono text-xs tracking-widest text-white/50">CARBS</div>
          <div className="stat-num text-3xl" style={{ color: "#007AFF" }}>{data.weekly_plan.week_totals.carbs}g</div>
        </div>
        <div>
          <div className="font-mono text-xs tracking-widest text-white/50">FAT</div>
          <div className="stat-num text-3xl" style={{ color: "#F59E0B" }}>{data.weekly_plan.week_totals.fat}g</div>
        </div>
      </div>

      {/* Day-swap notice — important for athletes with different competition schedules */}
      <div
        className="mt-6 p-4"
        style={{ background: "#0E1418", border: "1px solid #1E3548", borderLeft: "3px solid #007AFF" }}
        data-testid="weekly-swap-note"
      >
        <div className="font-mono text-[10px] tracking-widest text-[#007AFF] mb-2">ⓘ {lang === "ro" ? "INTERSCHIMBABILITATE" : "DAY SWAPPING"}</div>
        <p className="text-sm text-white/80 leading-relaxed m-0">{t.plan.week_swap_note}</p>
      </div>

      <MethodologyDisclaimer />
    </div>
  );
};

export default WeeklyPlan;
